import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  DeleteInviteAgentValidationSchema,
  TDeleteInviteAgentValidation,
  useDeleteInviteAgent,
} from '../../../api/delete-invite-agent.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INVITE_AGENT_QUERY_KEY } from '../../../api/get-list-invite-agent.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

export type TDeleteInviteAgentModalType = 'DeleteInviteAgentModal';
export interface IDeleteInviteAgentModalProps {
  invitationId: string;
}

const DeleteInviteAgentModal = memo(({ invitationId }: IDeleteInviteAgentModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteInviteAgentModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('DeleteInviteAgentModal');
  }, [userModalRootActions]);

  const form = useForm<TDeleteInviteAgentValidation>({
    resolver: zodResolver(DeleteInviteAgentValidationSchema),
    mode: 'onChange',
    defaultValues: {
      invitationId: invitationId || '',
    },
  });

  useEffect(() => {
    if (invitationId) {
      form.reset({
        invitationId: invitationId || '',
      });
    }
  }, [invitationId, form]);

  const mutationDeleteInvitation = useDeleteInviteAgent({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error delete invitation, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INVITE_AGENT_QUERY_KEY] })]);
        userModalRootActions.closeModal('DeleteInviteAgentModal');
        showToast({
          message: 'Invitation deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteInvitation = useCallback(
    (data: TDeleteInviteAgentValidation) => {
      mutationDeleteInvitation.mutate({
        invitationId: data?.invitationId || '',
      });
    },
    [mutationDeleteInvitation]
  );

  const handleClose = useCallback(() => {
    userModalRootActions.closeModal('DeleteInviteAgentModal');
  }, [userModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteInviteAgentModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Invitation</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteInvitation)(e);
          }}
          id="delete-invite-agent-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this invitation?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-invite-agent-form" type="submit" disabled={mutationDeleteInvitation.isPending}>
          {mutationDeleteInvitation.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

DeleteInviteAgentModal.displayName = 'DeleteInviteAgentModal';
export default DeleteInviteAgentModal;
